import React, { useState } from "react";
import styled from "styled-components";
import { Crosshair, Map, List, Sliders, ChevronRight } from "react-feather";
import { AutoComplete } from "./autocomplete";
import { Container, Row, Col } from "react-awesome-styled-grid";
import { useMediaQuery } from "react-responsive";

import {
  Button,
  FarmersBranchLogo,
  palette,
  fonts,
  mediaMin,
} from "../ui-components";

const Header = ({
  cuisines,
  isMapView,
  onViewChangeClick,
  onNearMeClick,
  onFilterApplyClick,
  onOriginSelection,
}) => {
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [filters, setFilters] = useState([]);
  const [options, setOptions] = useState([]);
  const [filterByOpen, setFilterByOpen] = useState(false);
  const isMobile = useMediaQuery({ query: `(max-width: 560px)` });

  const renderFiltersDropdown = () => (
    <FilterDropdownContainer>
      <FilterWrapper>
        <Row>
          <Col xs={4} sm={4}>
            <FilterTitle>Categories</FilterTitle>
            <ListWrapper>
              {cuisines.map((item) => (
                <CategoryContainer key={item}>
                  <label>
                    <CheckBox
                      value={item}
                      name={item}
                      type={"checkbox"}
                      onChange={(e) =>
                        e.target.checked
                          ? setFilters([...filters, item])
                          : setFilters(
                              filters.filter((keyword) => keyword !== item)
                            )
                      }
                      checked={filters.includes(item)}
                    />
                    {item}
                  </label>
                </CategoryContainer>
              ))}
            </ListWrapper>
          </Col>
          <Col xs={4} sm={4}>
            <FilterTitle>Options</FilterTitle>
            <GridContainer>
              <Row>
                <Col xs={4} sm={4} md={4} lg={6} xl={6}>
                  <CategoryContainer>
                    <label>
                      <CheckBox
                        value="delivery"
                        name="delivery"
                        type={"checkbox"}
                        onChange={(e) =>
                          e.target.checked
                            ? setOptions([...options, "delivery"])
                            : setOptions(
                                options.filter(
                                  (keyword) => keyword !== "delivery"
                                )
                              )
                        }
                        checked={options.includes("delivery")}
                      />
                      Delivery
                    </label>
                  </CategoryContainer>
                  <CategoryContainer>
                    <label>
                      <CheckBox
                        value="takeout"
                        name="takeout"
                        type={"checkbox"}
                        onChange={(e) =>
                          e.target.checked
                            ? setOptions([...options, "takeout"])
                            : setOptions(
                                options.filter(
                                  (keyword) => keyword !== "takeout"
                                )
                              )
                        }
                        checked={options.includes("takeout")}
                      />
                      Takeout
                    </label>
                  </CategoryContainer>
                  <CategoryContainer>
                    <label>
                      <CheckBox
                        value="opennow"
                        name="opennow"
                        type={"checkbox"}
                        onChange={(e) =>
                          e.target.checked
                            ? setFilterByOpen(true)
                            : setFilterByOpen(false)
                        }
                        checked={filterByOpen}
                      />
                      Open Now
                    </label>
                  </CategoryContainer>
                </Col>
                <Col xs={4} sm={4} md={4} lg={6} xl={6}></Col>
              </Row>
            </GridContainer>
          </Col>
        </Row>
        <ButtonContainer>
          <ApplyFiltersButton
            onClick={() => {
              filters.length === 0 && options.length === 0
                ? onFilterApplyClick({ filterByOpen })
                : onFilterApplyClick({ filters, options, filterByOpen });
              setIsFiltersVisible(false);
            }}
          >
            Apply Filters
            <ChevronRight />
          </ApplyFiltersButton>
          <ClearButton
            onClick={() => {
              setOptions([]);
              setFilters([]);
              onFilterApplyClick({ filterByOpen: false });
              setIsFiltersVisible(false);
              setFilterByOpen(false);
            }}
          >
            Clear Filters
          </ClearButton>
        </ButtonContainer>
      </FilterWrapper>
    </FilterDropdownContainer>
  );

  return (
    <>
      <HeaderContainer>
        <LeftContainer>
          <LogoContainer>
            <FarmersBranchLogo />
          </LogoContainer>
          <LocationsContainer>
            <MobileButton onClick={() => onNearMeClick()}>
              <IconContainer>
                <Crosshair size={15} style={{ marginRight: 5 }} />
              </IconContainer>
              <span>Near Me</span>
            </MobileButton>
            <Spacer>or</Spacer>
            <AutoComplete onSelection={(value) => onOriginSelection(value)} />
          </LocationsContainer>
          <MobileButton
            backgroundColor={palette.dark}
            textColor={palette.white}
            hoverColor={palette.green}
            arrowColor={palette.white}
            isActive={isFiltersVisible}
            onClick={() => setIsFiltersVisible(!isFiltersVisible)}
          >
            <IconContainer>
              <Sliders size={15} />
            </IconContainer>
            <span>Filters</span>
          </MobileButton>
          {isFiltersVisible && !isMobile && renderFiltersDropdown()}
        </LeftContainer>
        <RightContainer>
          <Button
            backgroundColor={palette.white}
            textColor={palette.text}
            hoverColor={palette.primary}
            borderColor={palette.text}
            onClick={() => onViewChangeClick()}
            hoverTextColor={palette.white}
          >
            <IconContainer>
              {!isMapView ? <List size={15} /> : <Map size={15} />}
            </IconContainer>
            {!isMapView ? "List" : "Map"} View
          </Button>
        </RightContainer>
        {isFiltersVisible && isMobile && renderFiltersDropdown()}
      </HeaderContainer>
    </>
  );
};

const HeaderContainer = styled.div`
  position: relative;
  z-index: 9999999;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${palette.tan};
  color: ${palette.text};
  padding: 10px;
  box-shadow: 0px 3px 8px rgba(0, 0, 0, 0.16);
  height: 50px;

  ${mediaMin.tablet_lg`
    justify-content: space-between;
  `};
`;

const LeftContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const RightContainer = styled.div`
  display: none;

  ${mediaMin.tablet_lg`
    display: block;
  `};
`;

const Spacer = styled.div`
  font-family: ${fonts.primary};
  color: ${palette.primary};
  font-size: 20px;
`;

const LogoContainer = styled.div`
  display: none;

  ${mediaMin.tablet_lg`
    display: block;
  `};
`;

const LocationsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-flow: row-reverse;

  ${mediaMin.tablet_lg`
    flex-flow: row;
  `};
`;

const IconContainer = styled.div`
  margin-bottom: -2px;

  ${mediaMin.tablet_lg`
    margin-right: 8px;
  `};
`;

const MobileButton = styled(Button)`
  span {
    display: none;
    ${mediaMin.tablet_lg`
      display: block;
    `};
  }
`;

const GridContainer = styled(Container)`
  padding-left: 0 !important;
  padding-right: 0 !important;
`;

const FilterDropdownContainer = styled.div`
  z-index: -1;
  position: absolute;
  top: 68px;
  left: 0;
  right: 0;
  background-color: ${palette.tan};
  border-radius: 0 0 25px 25px;
  padding: 15px 15px 30px;
  box-shadow: 0px 3px 8px rgba(0, 0, 0, 0.16),
    inset 1px 10px 5px -6px rgba(0, 0, 0, 0.16);
`;

const FilterWrapper = styled.div`
  width: 100%;
  max-width: 920px;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  text-decoration: underline;
  font-size: 11px;
  text-transform: uppercase;
  display: flex;
  &:hover {
    cursor: pointer;
  }
`;

const FilterTitle = styled.h4`
  font-family: ${fonts.primary};
  color: ${palette.primary};
  font-size: 20px;
  line-height: 1.25;
  margin: 20px 0 10px;

  ${mediaMin.tablet_lg`
    margin: 10px 0;
  `};
`;

const CategoryContainer = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
`;

const CheckBox = styled.input`
  margin-right: 15px;
  border-radius: 3px;
  box-shadow: 0px 3px 8px rgba(0, 0, 0, 0.16);
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 30px;
  gap: 20px;
`;

const ApplyFiltersButton = styled(Button)`
  display: inline-flex !important;
`;

const ListWrapper = styled.div`
  column-count: 2;

  ${mediaMin.tablet_lg`
    border-right: 1px solid ${palette.lightGray};
  `};
`;

export { Header };
